<template>
    <Toast />
    <div class="grid">
        <div class="col-12">
            <div class="card">
                <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-3">
                    <div>
                        <Button icon="pi pi-arrow-left" label="Historial" class="p-button-text p-0 mb-2" @click="$router.push('/dashboard/inventario/conteo/historial')" />
                        <h5 class="m-0" v-if="count">Conteo #{{ count.id }}</h5>
                    </div>
                    <div class="flex gap-2 mt-2 md:mt-0" v-if="count && count.status === 'OPEN'">
                        <Button label="Aplicar ajustes" icon="pi pi-check-square" class="p-button-success" @click="openApply" />
                        <Button label="Anular" icon="pi pi-times" class="p-button-danger p-button-outlined" @click="voidDialog = true" />
                    </div>
                </div>

                <ProgressBar v-if="loading" mode="indeterminate" style="height: 4px" />

                <div v-if="!loading && count">
                    <div class="grid">
                        <div class="col-12 md:col-3">
                            <div class="text-color-secondary text-sm">Estado</div>
                            <Tag :value="statusLabel(count.status)" :severity="statusSeverity(count.status)" />
                        </div>
                        <div class="col-12 md:col-3">
                            <div class="text-color-secondary text-sm">Resultado</div>
                            <Tag :value="resultLabel(count.result)" :severity="resultSeverity(count.result)" />
                        </div>
                        <div class="col-12 md:col-3">
                            <div class="text-color-secondary text-sm">Fecha</div>
                            <div class="font-medium">{{ formatDate(count.createdAt) }}</div>
                        </div>
                        <div class="col-12 md:col-3">
                            <div class="text-color-secondary text-sm">Contado por</div>
                            <div class="font-medium">{{ count.creator }} <Tag :value="count.creatorRol" severity="info" class="ml-1" /></div>
                        </div>
                    </div>

                    <Message v-if="count.status === 'APPLIED'" severity="success" :closable="false" class="mt-3">
                        Ajustes aplicados el {{ formatDate(count.appliedAt) }} por {{ count.appliedBy }}.
                        <span v-if="count.applyNotes">Notas: {{ count.applyNotes }}</span>
                    </Message>
                    <Message v-if="count.status === 'VOIDED'" severity="error" :closable="false" class="mt-3">
                        Conteo anulado el {{ formatDate(count.voidedAt) }}<span v-if="count.voidedBy"> por {{ count.voidedBy }}</span>.
                        <span v-if="count.voidReason">Motivo: {{ count.voidReason }}</span>
                    </Message>

                    <div class="grid text-center mt-2">
                        <div class="col-6 md:col-3"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">Contados</div><div class="font-bold text-xl">{{ count.linesCounted }} / {{ count.linesTotal }}</div></div></div>
                        <div class="col-6 md:col-3"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">Faltantes</div><div class="font-bold text-xl text-red-500">{{ count.shortageCount }}</div></div></div>
                        <div class="col-6 md:col-3"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">Sobrantes</div><div class="font-bold text-xl text-blue-500">{{ count.overageCount }}</div></div></div>
                        <div class="col-6 md:col-3"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">Valor estimado</div><div class="font-bold text-xl" :class="diffClass(count.totalValueDifference)">{{ formatMoney(count.totalValueDifference) }}</div></div></div>
                    </div>

                    <h6 class="mt-4">Detalle por producto</h6>
                    <DataTable :value="count.lines" responsiveLayout="scroll" class="p-datatable-sm" sortField="categoryName" :sortOrder="1">
                        <Column field="productName" header="Producto">
                            <template #body="{ data }">
                                {{ data.productName }}
                                <i v-if="data.movedDuringCount" class="pi pi-clock text-orange-500 ml-1" v-tooltip.top="'Hubo ventas o compras mientras se contaba'"></i>
                            </template>
                        </Column>
                        <Column field="categoryName" header="Categoría" />
                        <Column header="Sistema"><template #body="{ data }">{{ formatQty(data.theoreticalStock) }}</template></Column>
                        <Column header="Físico"><template #body="{ data }">{{ formatQty(data.physicalStock) }}</template></Column>
                        <Column header="Diferencia">
                            <template #body="{ data }"><span :class="diffClass(data.difference)">{{ formatSignedQty(data.difference) }}</span></template>
                        </Column>
                        <Column header="Resultado">
                            <template #body="{ data }"><Tag :value="resultLabel(data.result)" :severity="resultSeverity(data.result)" /></template>
                        </Column>
                        <Column header="Valor est.">
                            <template #body="{ data }">
                                <span v-if="data.valueDifference !== null" :class="diffClass(data.valueDifference)">{{ formatMoney(data.valueDifference) }}</span>
                                <i v-if="data.valueUncertain" class="pi pi-question-circle ml-1 text-color-secondary" v-tooltip.top="'Sin compras con que valorarlo'"></i>
                            </template>
                        </Column>
                        <Column v-if="count.status === 'APPLIED'" header="Aplicación">
                            <template #body="{ data }">
                                <Tag v-if="data.applyStatus === 'APPLIED'" value="Aplicado" severity="success" />
                                <span v-else class="text-sm text-color-secondary">{{ skipReasonLabel(data.applySkipReason) }}</span>
                            </template>
                        </Column>
                    </DataTable>

                    <div v-if="count.notes" class="mt-3">
                        <h6>Notas</h6>
                        <div class="surface-100 border-round p-3">{{ count.notes }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Aplicar ajustes -->
        <Dialog v-model:visible="applyDialog" header="Aplicar ajustes al stock" modal :style="{ width: '960px' }" :breakpoints="{ '960px': '95vw' }">
            <ProgressBar v-if="loadingPreview" mode="indeterminate" style="height: 4px" />
            <div v-if="applyPreview && !loadingPreview">
                <p class="mt-0 text-sm">
                    A cada producto se le suma la <b>diferencia contada</b>, sobre el stock de hoy: las ventas hechas
                    después del conteo se conservan. Cada línea deja un ajuste con su valor y su asiento.
                </p>
                <Message v-if="applyPreview.stale" severity="warn" :closable="false">
                    Este conteo tiene {{ Math.round(applyPreview.ageHours) }} horas. Desde entonces el stock pudo cambiar por motivos que el conteo no vio.
                </Message>
                <Message v-if="hasMovedLines" severity="warn" :closable="false">
                    Algunos productos se vendieron o compraron mientras se contaba (<i class="pi pi-clock"></i>): revise que la diferencia tenga sentido.
                </Message>

                <DataTable :value="applyRows" responsiveLayout="scroll" class="p-datatable-sm">
                    <Column header="" style="width:3rem">
                        <template #body="{ data }">
                            <Checkbox v-if="data.applicable" v-model="data.selected" :binary="true" />
                        </template>
                    </Column>
                    <Column field="productName" header="Producto">
                        <template #body="{ data }">
                            {{ data.productName }}
                            <i v-if="data.movedDuringCount" class="pi pi-clock text-orange-500 ml-1"></i>
                        </template>
                    </Column>
                    <Column header="Diferencia">
                        <template #body="{ data }"><span :class="diffClass(data.countDifference)">{{ formatSignedQty(data.countDifference) }}</span></template>
                    </Column>
                    <Column header="Hoy"><template #body="{ data }">{{ formatQty(data.currentStock) }}</template></Column>
                    <Column header="Quedará en"><template #body="{ data }">{{ data.applicable ? formatQty(data.resultingStock) : '' }}</template></Column>
                    <Column header="Valor">
                        <template #body="{ data }">
                            <span v-if="data.applicable" :class="diffClass(data.estimatedValue)">{{ formatMoney(data.estimatedValue) }}</span>
                        </template>
                    </Column>
                    <Column header="Motivo">
                        <template #body="{ data }">
                            <Dropdown v-if="data.applicable" v-model="data.reasonCode" :options="reasonsFor(data.periodic)" optionLabel="label" optionValue="value" class="w-full reason-dropdown" />
                            <span v-else class="text-sm text-color-secondary">{{ skipReasonLabel(data.skipReason) }}</span>
                        </template>
                    </Column>
                </DataTable>

                <div class="flex justify-content-between align-items-center mt-3">
                    <span class="text-sm">{{ selectedRows.length }} de {{ applyPreview.applicableCount }} producto(s) seleccionados</span>
                    <span class="font-bold">Total estimado: <span :class="diffClass(selectedTotal)">{{ formatMoney(selectedTotal) }}</span></span>
                </div>
                <div class="field mt-3">
                    <label class="block text-sm" for="applyNotes">Notas (opcional)</label>
                    <Textarea id="applyNotes" v-model="applyNotes" rows="2" class="w-full" />
                </div>
            </div>
            <template #footer>
                <Button label="Cancelar" class="p-button-text" @click="applyDialog = false" />
                <Button :label="applyPreview && applyPreview.applicableCount === 0 ? 'Cerrar el conteo' : 'Aplicar'" icon="pi pi-check" class="p-button-success" :loading="applying"
                        :disabled="!applyPreview || (applyPreview.applicableCount > 0 && selectedRows.length === 0)" @click="doApply" />
            </template>
        </Dialog>

        <Dialog v-model:visible="voidDialog" header="Anular conteo" modal :style="{ width: '420px' }">
            <p>Indique la razón. El conteo queda en el historial como anulado y no se podrá aplicar.</p>
            <Textarea v-model="voidReason" rows="3" class="w-full" />
            <template #footer>
                <Button label="Cancelar" class="p-button-text" @click="voidDialog = false" />
                <Button label="Anular" class="p-button-danger" :loading="voiding" @click="doVoid" />
            </template>
        </Dialog>
    </div>
</template>

<script>
import httpRequest from '../../../service/httpRequest';
import {
    diffClass, formatDate, formatMoney, formatQty, formatSignedQty, resultLabel, resultSeverity,
    reasonsFor, skipReasonLabel, statusLabel, statusSeverity,
} from './inventoryFormat';

export default {
    name: 'InventoryCountDetail',
    data() {
        return {
            loading: false,
            count: null,
            applyDialog: false,
            loadingPreview: false,
            applyPreview: null,
            applyRows: [],
            applyNotes: '',
            applying: false,
            voidDialog: false,
            voidReason: '',
            voiding: false,
        };
    },
    created() {
        if (!this.$can('inventoryManage')) {
            this.$router.replace('/dashboard/inventario/conteo');
            return;
        }
        const id = this.$route.query.id;
        if (!id) {
            this.$router.replace('/dashboard/inventario/conteo/historial');
            return;
        }
        this.load(id);
    },
    computed: {
        selectedRows() {
            return this.applyRows.filter(r => r.applicable && r.selected);
        },
        selectedTotal() {
            return this.selectedRows.reduce((sum, r) => sum + Number(r.estimatedValue || 0), 0);
        },
        hasMovedLines() {
            return this.applyRows.some(r => r.applicable && r.movedDuringCount);
        },
    },
    methods: {
        diffClass, formatDate, formatMoney, formatQty, formatSignedQty, resultLabel, resultSeverity,
        reasonsFor, skipReasonLabel, statusLabel, statusSeverity,
        async load(id) {
            this.loading = true;
            try {
                const r = await httpRequest.getInventoryCount(id);
                if (r.status === 200 && r.data) {
                    this.count = r.data;
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudo cargar', life: 4000 });
                }
            } catch (e) {
                console.error(e);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.loading = false;
            }
        },
        async openApply() {
            this.applyDialog = true;
            this.applyNotes = '';
            this.applyPreview = null;
            this.loadingPreview = true;
            try {
                const r = await httpRequest.getInventoryCountApplyPreview(this.count.id);
                if (r.status === 200 && r.data) {
                    this.applyPreview = r.data;
                    this.applyRows = (r.data.lines || [])
                        .filter(l => l.result !== 'NOT_COUNTED' && l.result !== 'BALANCED')
                        .map(l => ({ ...l, selected: l.applicable, reasonCode: l.defaultReasonCode }));
                } else {
                    this.applyDialog = false;
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudo preparar la aplicación', life: 4000 });
                }
            } catch (e) {
                console.error(e);
                this.applyDialog = false;
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.loadingPreview = false;
            }
        },
        async doApply() {
            this.applying = true;
            try {
                const lineIds = this.selectedRows.map(r => r.lineId);
                const reasonCodes = {};
                for (const row of this.selectedRows) reasonCodes[row.lineId] = row.reasonCode;
                const r = await httpRequest.applyInventoryCount(this.count.id, lineIds, reasonCodes, this.applyNotes);
                if (r.status === 200 && r.data) {
                    this.count = r.data;
                    this.applyDialog = false;
                    this.$toast.add({ severity: 'success', summary: 'Ajustes aplicados', detail: `${lineIds.length} producto(s) ajustados`, life: 4000 });
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudo aplicar', life: 5000 });
                }
            } catch (e) {
                console.error(e);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.applying = false;
            }
        },
        async doVoid() {
            this.voiding = true;
            try {
                const r = await httpRequest.voidInventoryCount(this.count.id, this.voidReason);
                if (r.status === 200 && r.data) {
                    this.count = r.data;
                    this.voidDialog = false;
                    this.$toast.add({ severity: 'success', summary: 'Conteo anulado', life: 3000 });
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudo anular', life: 4000 });
                }
            } catch (e) {
                console.error(e);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.voiding = false;
            }
        },
    },
};
</script>

<style scoped>
.reason-dropdown {
    min-width: 12rem;
}
</style>
